import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, ChevronDown, ChevronUp, Edit, Trash2 } from 'lucide-react';
import VitalsDisplay from '@/components/VitalsDisplay';

const VisitHistory = ({ visits, onEdit, onDelete }) => {
  const [expandedVisit, setExpandedVisit] = useState(null);

  if (!visits || visits.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-12 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>Aucune consultation enregistrée</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="space-y-4" data-testid="visit-history">
      {visits.map((visit) => {
        const isExpanded = expandedVisit === visit.id;

        return (
          <Card key={visit.id} className="card-hover" data-testid={`visit-card-${visit.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div 
                  className="flex-1 cursor-pointer"
                  onClick={() => setExpandedVisit(isExpanded ? null : visit.id)}
                >
                  <CardTitle className="text-lg">{visit.motif}</CardTitle>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                    <span>{formatDate(visit.date)}</span>
                    {visit.diagnostic && visit.diagnostic.length > 0 && (
                      <span className="text-blue-600 font-medium">
                        {visit.diagnostic.join(', ')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(visit);
                    }}
                    data-testid={`edit-visit-${visit.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Êtes-vous sûr de vouloir supprimer cette consultation ?')) {
                        onDelete(visit.id);
                      }
                    }}
                    className="hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                    data-testid={`delete-visit-${visit.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600 transition-colors p-2"
                    onClick={() => setExpandedVisit(isExpanded ? null : visit.id)}
                    data-testid={`toggle-visit-${visit.id}`}
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </CardHeader>

            {isExpanded && (
              <CardContent className="border-t pt-6 space-y-6 animate-slide-in" data-testid={`visit-details-${visit.id}`}>
                {/* SOAP Notes */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">S - Subjectif</h4>
                    <p className="text-sm text-blue-800">{visit.soap.subjectif || 'Non renseigné'}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-semibold text-green-900 mb-2">O - Objectif</h4>
                    <p className="text-sm text-green-800">{visit.soap.objectif || 'Non renseigné'}</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <h4 className="font-semibold text-orange-900 mb-2">A - Assessment</h4>
                    <p className="text-sm text-orange-800">{visit.soap.assessment || 'Non renseigné'}</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-semibold text-purple-900 mb-2">P - Plan</h4>
                    <p className="text-sm text-purple-800">{visit.soap.plan || 'Non renseigné'}</p>
                  </div>
                </div>

                {/* Vitals */}
                {visit.vitals && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Constantes</h4>
                    <VitalsDisplay vitals={visit.vitals} />
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default VisitHistory;
